/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useApproveQueryMutation,
  useGetOneQueryQuery,
  useReproveQueryMutation,
} from "@/services/it/updates/query-update";
import { Badge, BadgeProps } from "components/ui/badge";
import { DialogClose, DialogFooter } from "components/ui/dialog";
import { Textarea } from "components/ui/textarea";
import { ApprovalModal } from "../modal/approval-modal";
import { ReprovalModal } from "../modal/reproval-modal";
import {
  Avatar,
  AvatarFallback,
} from "components/ui/avatar";
import { QueryStatusEnum } from "@/enum/queryStatus";
import { Button } from "components/ui/button";
import { useState } from "react";
import { DynamicTable } from "./dynamic-table";

export function QueryReviewModal({
  queryId,
  onClose,
}: {
  queryId: string;
  onClose: () => void;
}) {
  const { data: oneQuery } = useGetOneQueryQuery({
    id: queryId,
  });

  const [approveQuery] = useApproveQueryMutation();
  const [reproveQuery] = useReproveQueryMutation();

  const [comment, setComment] = useState<string>("");

  const QueryStatusLabels: Record<QueryStatusEnum, string> = {
    [QueryStatusEnum.AUTO_APPROVED]: "Aprovado Automaticamente",
    [QueryStatusEnum.APPROVED]: "Aprovado",
    [QueryStatusEnum.PENDING]: "Pendente",
    [QueryStatusEnum.REPROVED]: "Reprovado",
  };

  const queryStatusConfig: Record<
    QueryStatusEnum,
    { text: string; variant: BadgeProps["variant"] }
  > = {
    [QueryStatusEnum.APPROVED]: { text: "Aprovado", variant: "approved" },
    [QueryStatusEnum.PENDING]: { text: "Pendente", variant: "pending" },
    [QueryStatusEnum.REPROVED]: { text: "Reprovado", variant: "reproved" },
    [QueryStatusEnum.AUTO_APPROVED]: {
      text: "Aprovado Automaticamente",
      variant: "autoApproved",
    },
  };

  const getStatusLabel = (status: QueryStatusEnum | undefined): string => {
    if (!status) return "Desconhecido";
    return QueryStatusLabels[status] || "Desconhecido";
  };

  const statusConfig = queryStatusConfig[oneQuery?.status as QueryStatusEnum];

  const getInitials = (name?: string) => {
    if (!name) return "";
    const [firstName, lastName] = name.split(" ");
    return lastName
      ? `${firstName?.charAt(0)}${lastName?.charAt(0)}`
      : `${firstName?.charAt(0)}`;
  };

  const handleApproveQuery = async (id: string) => {
    try {
      await approveQuery({ id: String(id), comment }).unwrap();
      console.log(`Query ${id} aprovada`);
      onClose();
    } catch (error) {
      console.error("Algo não saiu como esperado!", error);
    }
  };

  const handleReproveQuery = async (id: string) => {
    try {
      await reproveQuery({ id: String(id), comment }).unwrap();
      console.log(`Query ${id} reprovada`);
      onClose();
    } catch (error) {
      console.error("Algo não saiu como esperado!", error);
    }
  };

  return (
    <div className="lg:min-w-[700px]">
      <div className="flex justify-between items-start">
        <div className="flex items-center space-x-3">
          <Avatar className="h-10 w-10">
            <AvatarFallback>
              {getInitials(oneQuery?.responsible.name)}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium text-sm">{oneQuery?.responsible?.name}</p>
            <p className="text-sm text-muted-foreground">Solicitante</p>
          </div>
        </div>
        <div className="text-right">
          <p className="font-medium text-sm">
            {new Date(oneQuery?.created_at)
              .toLocaleString("pt-BR")
              .replace(", ", " - ")}
          </p>
          <p className="text-sm text-muted-foreground">Data da Query</p>
        </div>
      </div>

      <div className="flex items-center space-x-3 my-3">
        <div>
          <p className="text-sm">
            {oneQuery?.status === QueryStatusEnum.AUTO_APPROVED
              ? "Aprovado Automaticamente"
              : oneQuery?.approval_log?.approval_responsible?.name || "-"}
          </p>
          <p className="text-sm text-muted-foreground">Aprovador</p>
        </div>
      </div>

      <hr className="my-2" />
      <div className="w-full h-auto">
        <div className="flex gap-3 h-32 w-full">
          <div className="w-full flex flex-col gap-2">
            <label htmlFor="" className="text-sm">
              Query Solicitada:
            </label>
            <Textarea
              className="bg-[#F9FAFB] Consolas border-black/20 border "
              defaultValue={oneQuery?.query}
              disabled
            />
          </div>
          <div className="w-full flex flex-col gap-2">
            <label htmlFor="" className="text-sm">
              {oneQuery?.status === QueryStatusEnum.PENDING
                ? `Escreva um comentário: (Opcional)`
                : "Comentário do Avaliador: "}
            </label>
            {oneQuery?.status === QueryStatusEnum.PENDING ? (
              <Textarea
                className="border-black/20 border"
                placeholder="Escreva um feedback"
                value={comment}
                defaultValue={oneQuery?.approval_log?.feedback}
                onChange={(e) => setComment(e.target.value)}
              />
            ) : (
              <Textarea
                className="border-black/20 border"
                placeholder="A query já foi avaliada, mas o avaliador não forneceu feedback ou a aprovação foi feita automaticamente."
                disabled
                value={comment}
                defaultValue={oneQuery?.approval_log?.feedback}
                onChange={(e) => setComment(e.target.value)}
              />
            )}
          </div>
        </div>
      </div>

      <hr className="my-2" />

      <div className="w-full">
        <label htmlFor="" className="text-sm">
          Itens que serão modificados:
        </label>
        <DynamicTable data={oneQuery?.result_before} />
        <div className="flex w-full flex-col md:flex-row justify-between mb-2 gap-2 pt-2">
          <div className="text-sm">
            Banco Afetado:
            <strong> {oneQuery?.query_connection?.connection_nickname} </strong>
            | Host:
            <strong> {oneQuery?.query_connection?.connection_host} </strong>
          </div>

          <div className="flex gap-2">
            <Badge
              className={`hover:bg-opacity-80 cursor-default text-black`}
              variant={statusConfig?.variant}
            >
              {getStatusLabel(oneQuery?.status)}
            </Badge>
            <Badge className="hover:bg-[#f5cb2b]/80 text-black cursor-default">
              {oneQuery?.affected_rows} Linhas Afetadas
            </Badge>
          </div>
        </div>
      </div>

      {oneQuery?.status === QueryStatusEnum.PENDING ? (
        <DialogFooter>
          <DialogClose asChild>
            <ReprovalModal
              id={queryId}
              onConfirm={(id) => {
                handleReproveQuery(id);
              }}
              onClose={onClose}
            />
          </DialogClose>
          <ApprovalModal
            id={queryId}
            onConfirm={handleApproveQuery}
            onClose={onClose}
          />
        </DialogFooter>
      ) : (
        <DialogFooter>
          <DialogClose asChild>
            <Button
              type="button"
              className="bg-transparent border border-black/20 text-black rounded-md font-semibold text-sm p-2 px-4 flex items-center gap-2 hover:bg-black/10"
            >
              Sair
            </Button>
          </DialogClose>
        </DialogFooter>
      )}
    </div>
  );
}
